import React from "react";
import { Heart, Users, Award, BookOpen, Sparkles, Building2 } from "lucide-react";
import { PageHero, Section, Card } from "@/components/Section";
import { IMAGES } from "@/lib/site";

const equipe = [
  { nom: "Madame Lefèvre", role: "Cheffe d'établissement" },
  { nom: "Monsieur Marchand", role: "Responsable pédagogique" },
  { nom: "Madame Bertin", role: "Conseillère principale d'éducation" },
  { nom: "Madame Renault", role: "Coordinatrice des stages" },
];

const valeurs = [
  { icon: Heart, t: "Bienveillance", d: "Une posture éducative fondée sur l'écoute et le respect." },
  { icon: Users, t: "Communauté", d: "Une vie scolaire chaleureuse, en classe comme dans les couloirs." },
  { icon: Sparkles, t: "Inclusion", d: "Une école ouverte, où chaque parcours est valorisé." },
  { icon: Award, t: "Exigence positive", d: "Croire en chaque élève et l'amener vers la réussite." },
];

export default function LeLycee() {
  return (
    <div data-testid="le-lycee-page">
      <PageHero
        eyebrow="Le Lycée"
        title="Un établissement à taille humaine, fidèle à l'esprit de Joseph Wresinski"
        subtitle="Depuis sa création, notre lycée porte une conviction : la réussite scolaire et professionnelle ne se décrète pas, elle se construit avec patience, respect et accompagnement."
        image={IMAGES.campus}
      />

      <Section
        eyebrow="Notre histoire"
        title="Une mission éducative au service de tous les jeunes"
      >
        <div className="grid gap-12 lg:grid-cols-[1.3fr_1fr] lg:items-start">
          <div className="space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>
              Notre établissement tire son nom de Joseph Wresinski, fondateur du
              mouvement ATD Quart Monde, dont l'engagement pour la dignité de
              chaque personne inspire notre projet éducatif.
            </p>
            <p>
              Implanté à Angers, le lycée accueille chaque année des jeunes aux
              parcours variés, en leur proposant des formations professionnelles
              concrètes et reconnues. Nous croyons que la réussite est possible
              pour chacun, à condition d'être accompagné, encouragé et soutenu.
            </p>
            <p>
              Notre projet repose sur trois piliers : un enseignement de qualité,
              un accompagnement éducatif renforcé et un lien étroit avec le monde
              de l'entreprise.
            </p>
          </div>
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.values} alt="Vie au lycée" className="h-full w-full object-cover" />
          </div>
        </div>
      </Section>

      <Section
        wrapper="bg-brand-sand-wrapper/40"
        eyebrow="Nos valeurs"
        title="Ce qui guide notre quotidien"
      >
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {valeurs.map((v, i) => (
            <Card key={v.t} data-testid={`valeur-lycee-${i}`}>
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-slate/10 text-brand-slate">
                <v.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">
                {v.t}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {v.d}
              </p>
            </Card>
          ))}
        </div>
      </Section>

      <Section eyebrow="L'équipe pédagogique" title="Des adultes engagés à vos côtés">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {equipe.map((p, i) => (
            <Card key={p.nom} data-testid={`equipe-${i}`} className="text-center">
              <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-brand-sage/20 text-brand-sage-dark">
                <BookOpen className="h-7 w-7" />
              </div>
              <p className="mt-5 font-heading text-lg font-semibold text-brand-slate-dark">
                {p.nom}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{p.role}</p>
            </Card>
          ))}
        </div>
        <p className="mt-8 text-sm text-muted-foreground">
          Et une équipe complète d'enseignants, d'éducateurs et de personnels
          administratifs au service de votre enfant.
        </p>
      </Section>

      <Section wrapper="bg-brand-slate text-brand-cream">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-sand">
              Cadre de vie
            </p>
            <h2 className="mt-4 font-heading text-3xl font-bold sm:text-4xl">
              Un lieu où l'on se sent bien
            </h2>
            <p className="mt-5 text-base leading-relaxed text-brand-cream/85">
              Nos locaux ont été pensés pour favoriser la concentration, la
              collaboration et le respect mutuel. Salles modernes, ateliers
              équipés, espaces de détente et de restauration : tout est mis en
              œuvre pour que les jeunes vivent leurs années lycée dans les
              meilleures conditions.
            </p>
            <ul className="mt-6 grid gap-3 text-sm text-brand-cream/90">
              {[
                "Salles équipées et ateliers professionnels modernes",
                "Cour, espaces verts et salle de détente",
                "Restauration sur place avec une cuisine maison",
                "Internat partenaire à proximité",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-sand" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="overflow-hidden rounded-3xl">
            <img src={IMAGES.classroom} alt="Salle de classe lumineuse" className="h-full w-full object-cover" />
          </div>
        </div>
      </Section>
    </div>
  );
}
